import React from "react";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import styled from "styled-components";
import { Button } from "@material-ui/core";
import moment from "moment";

const Title = styled.h2`
  margin-right: 18px;
  font-size: 18px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.white};
`;

/**
 * Flatten any structure into rows for Excel.
 * - Arrays of objects => explode into multiple rows (cartesian product).
 * - Arrays of primitives => join with ", ".
 * - Objects => flattened into dot paths.
 */

const isObj = (v: any) => v && typeof v === "object" && !Array.isArray(v);

type Row = Record<string, any>;

/** Flatten a non-array object into dot paths (no array expansion here) */
function flattenObject(obj: any, prefix = "", out: Row = {}): Row {
  if (!isObj(obj)) return out;
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (isObj(v)) {
      flattenObject(v, key, out);
    } else if (Array.isArray(v)) {
      // primitives => join; objects handled at a higher level (explode)
      if (v.every((x) => !isObj(x))) {
        out[key] = v.map((x) => (x == null ? "" : String(x))).join(", ");
      } else {
        // leave as-is; expansion happens in expandToRows
        out[key] = v;
      }
    } else {
      out[key] = v;
    }
  }
  return out;
}

/** Cartesian product merge of two row sets */
function productMerge(a: Row[], b: Row[]): Row[] {
  if (a.length === 0) return b;
  if (b.length === 0) return a;
  const out: Row[] = [];
  for (const ra of a) {
    for (const rb of b) out.push({ ...ra, ...rb });
  }
  return out;
}

/**
 * Expand arrays-of-objects into multiple rows.
 * Strategy:
 * 1) Flatten primitives/objects first; keep any arrays-of-objects aside.
 * 2) For each array-of-objects, recursively expand its elements into rows and product-merge.
 */
function expandToRows(input: any, basePath = ""): Row[] {
  // Normalize to array at root
  const roots = Array.isArray(input) ? input : [input];

  const rows: Row[] = [];

  for (const root of roots) {
    if (!isObj(root)) {
      // primitive at root → single row with a 'value' column
      rows.push({ value: root });
      continue;
    }

    // Step 1: flatten object ignoring arrays-of-objects (they remain as arrays)
    const flat = flattenObject(root);

    // Split fields into: simpleFields, arraysOfObjects
    const simple: Row = {};
    const arraysToExplode: Array<{ key: string; arr: any[] }> = [];

    for (const [k, v] of Object.entries(flat)) {
      if (Array.isArray(v) && v.some((x) => isObj(x))) {
        arraysToExplode.push({ key: k, arr: v as any[] });
      } else {
        // numbers/strings/dates/null/arrays of primitives already joined by flattenObject
        simple[k] = v;
      }
    }

    // Start with one base row (just the simple fields)
    let acc: Row[] = [simple];

    // Step 2: explode each array-of-objects (cartesian product if many)
    for (const { key, arr } of arraysToExplode) {
      // Each element can be a primitive or object; we’ll flatten it and prefix with the array key + index if needed
      const perElemRows: Row[] = [];

      for (const elem of arr) {
        if (isObj(elem)) {
          // Flatten element; keep its own nested arrays for further expansion
          const elemFlat = flattenObject(elem, key);
          // Now, if elemFlat still contains arrays-of-objects (deeply), handle them by a recursive call:
          const deepExpanded = expandToRows(elem); // rows for the element itself
          if (deepExpanded.length > 0 && Object.keys(deepExpanded[0]).length) {
            // Prefix deepExpanded keys with `key` when merging
            for (const r of deepExpanded) {
              const prefixed: Row = {};
              for (const [rk, rv] of Object.entries(r)) {
                prefixed[`${key}.${rk}`] = rv;
              }
              perElemRows.push(prefixed);
            }
          } else {
            // No deeper arrays-of-objects; use the simple flattened elem
            perElemRows.push(elemFlat);
          }
        } else {
          // Primitive element → put under "key[]"
          perElemRows.push({ [`${key}[]`]: elem });
        }
      }

      acc = productMerge(acc, perElemRows);
    }

    rows.push(...acc);
  }

  return rows;
}

/** Public: plug into your export flow */
function formatOrderByDate(values: any): any[] {
  try {
    const rows = expandToRows(values);

    // Optional: normalize any date-looking fields via moment (YYYY-MM-DD HH:mm)
    const DATE_LIKE = /(date|created|updated|timestamp|time|eta)$/i;
    return rows.map((r) => {
      const out: Row = {};
      for (const [k, v] of Object.entries(r)) {
        if (v == null) {
          out[k] = "";
          continue;
        }
        if (typeof v === "string" || typeof v === "number") {
          if (DATE_LIKE.test(k)) {
            const m = moment(v);
            out[k] = m.isValid() ? m.format("YYYY-MM-DD HH:mm") : String(v);
          } else {
            out[k] = v;
          }
        } else {
          out[k] = Array.isArray(v) ? v.join(", ") : JSON.stringify(v);
        }
      }
      return out;
    });
  } catch (e) {
    console.warn("formatOrderByDate error:", e);
    // Fallback: best-effort single row
    return Array.isArray(values) ? values : [values];
  }
}

const ExportExcel = ({ values, title = "Customers" }) => {
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";

  const exportToCSV = (csvData: any, fileName: string) => {
    const dataRows = formatOrderByDate(csvData);
    const ws = XLSX.utils.json_to_sheet(dataRows.length ? dataRows : [{}]);
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(data, fileName + fileExtension);
  };

  return (
    <Button
      style={{ marginTop: 8 }}
      color="primary"
      variant="outlined"
      onClick={() =>
        exportToCSV(
          values,
          `${title} - ${new Date().toJSON().slice(0, 10).replace(/-/g, "/")}`
        )
      }
    >
      Export Excel
    </Button>
  );
};

export default ExportExcel;
